import { ERROR_MESSAGES } from '@tebuto/functions/api'
import { DateTime } from 'luxon'

/*

The Widget has its own type definitions to decouple it from the rest of the application as best as possible.
This is needed to properly bundle the booking widget and avoid any unwanted dependencies.

*/

export enum EventLocationSelection {
    VIRTUAL = 'virtual',
    ONSITE = 'onsite'
}

export enum EventLocation {
    VIRTUAL = 'virtual',
    ONSITE = 'onsite',
    NOT_FIXED = 'not-fixed'
}

export enum OutageFeeApplies {
    YES = 'yes',
    NO = 'no',
    NOT_SELECTED = 'not-selected'
}

export enum PaymentType {
    ONLINE = 'online',
    CASH = 'cash',
    INVOICE = 'invoice'
}

export enum OnlinePaymentMethod {
    CARD = 'card',
    SEPA = 'sepa',
    PAYPAL = 'paypal',
    KLARNA = 'klarna',
    BANK_TRANSFER = 'bank_transfer',
    APPLE_PAY = 'apple_pay',
    GOOGLE_PAY = 'google_pay'
}

export type PaymentTypes = Array<'online' | 'cash' | 'invoice'>
export type OnlinePaymentMethods = Array<
    'card' | 'sepa' | 'paypal' | 'klarna' | 'bank_transfer' | 'apple_pay' | 'google_pay'
>
export type PaymentStatus = 'open' | 'pending' | 'paid' | 'failed' | 'cancelled' | 'refunded'
export type PaymentConfiguration = {
    paymentTypes: PaymentTypes
    onlinePaymentMethods: OnlinePaymentMethods
    onlineProviderType?: 'paypal' | 'stripe' | null
    stripeConnectedAccountId?: string | null
    stripePublishableKey?: string | null
}

export type TherapistContactInfo = {
    type: string
    value: string
}

export type Therapist = {
    name: string
    firstName: string | null
    lastName: string | null
    title?: string | null
    slug?: string
    image?: string | null
    description?: string | null
    address: {
        streetAndNumber: string
        additionalInformation: string | null
        city: {
            name: string
            zip: string
        }
    }
    contactInfos?: TherapistContactInfo[]
    showWatermark?: boolean
    features?: {
        featurePaymentEnabled?: boolean
    }
    settings?: {
        featurePaymentEnabled?: boolean
        dataPrivacyAgreementUrl?: string | null
        termsAndConditionsUrl?: string | null
    }
}

export type EventTherapist = {
    id: number
    uuid: string
    name: string
    firstName: string | null
    lastName: string | null
    address: {
        streetAndNumber: string
        additionalInformation: string | null
        city: {
            name: string
            zip: string
        }
    } | null
}

export type EventCategory = {
    id: number
    name: string
    description?: string | null
    color: string
    price: string
    taxRate: string
    duration?: number
    location: EventLocation
    onlineMeetingProvider?: MeetingProvider
    outageFeeEnabled: boolean
    outageFeeHours?: number
    outageFeePrice?: number
    paymentEnabled?: boolean
    paymentDuringBooking?: boolean
}

export enum MeetingProvider {
    ZOOM = 'zoom',
    MICROSOFT_TEAMS = 'microsoft-teams',
    TEBUTO_MEET = 'tebuto-meet'
}

export type BookableEvent = {
    start: string
    end: string
    customTitle: string
    ruleId: number
    categoryId: number
    ruleCategoryIds?: number[]
    therapist?: EventTherapist
    /** Effective category location from the event owner (public widget API). */
    location?: EventLocation
    onlineMeetingProvider?: MeetingProvider
    /** Event-owner therapist: payments feature (public widget API). */
    featurePaymentEnabled?: boolean
}

export type ParsedBookableEvent = BookableEvent & {
    startDatetime: DateTime<true>
    endDatetime: DateTime<true>
    therapist?: EventTherapist
}

export type Booking = {
    id: number
    start: string
    createdAt: string
    locationSelection: EventLocationSelection
    isConfirmed: boolean
    isOutage: boolean
    outageFeeApplies: OutageFeeApplies | null
    price: string
    outageFeePrice: string | null
    taxRate: string
    ics: string
    paymentStatus?: PaymentStatus | null
    // event: ClientEvent
    // onlineMeeting?: OnlineMeeting
}

export type BookEventPayload = {
    eventRuleId: number
    start: DateTime<true>
    end: DateTime<true>
    locationSelection?: EventLocationSelection
}

export type CreditOverviewItem = {
    categoryId: number
    categoryName: string
    remaining: number
    total: number
    used: number
}

export type APIError = {
    type: keyof typeof ERROR_MESSAGES
}
